import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Database, DownloadSimple, UploadSimple, Warning, CheckCircle, Envelope, ShareNetwork, ArrowsClockwise, Copy } from '@phosphor-icons/react';
import { API } from './adminConstants';

const COLLECTION_LABELS = {
  uploaded_images: 'Uploaded Images & Icons',
  uploaded_pdfs: 'Uploaded PDFs',
  news_images: 'News Photos',
  cms_pages: 'Page Content (Hero, About, Apprenticeships)',
  cms_services: 'Services',
  cms_news: 'News Stories',
  cms_community: 'Community Articles',
  cms_case_studies: 'Case Studies',
  cms_testimonials: 'Testimonials',
  cms_locations: 'Locations (Map Pins)',
  jobs: 'Job Listings',
  service_types: 'Contact Form Service Types',
  driver_training_courses: 'Driver Training Courses',
  query_categories: 'Query Categories',
  footer_documents: 'Footer Documents (PDFs)',
  footer_partners: 'Footer Partners / Awards',
  departments: 'Departments',
  announcements: 'Announcements',
  hr_documents: 'HR Documents',
};

export function DataExportImport() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [bundleInfo, setBundleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [bundleSyncing, setBundleSyncing] = useState(false);
  const [confirmBundleSync, setConfirmBundleSync] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [emailDiag, setEmailDiag] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);

  // Cross-environment recovery (server-to-server pull via one-time token).
  const [mintingToken, setMintingToken] = useState(false);
  const [recoveryUrl, setRecoveryUrl] = useState('');
  const [recoveryExpiresAt, setRecoveryExpiresAt] = useState(null);
  const [pullUrl, setPullUrl] = useState('');
  const [pulling, setPulling] = useState(false);
  const [confirmPull, setConfirmPull] = useState(false);
  const [pullProgress, setPullProgress] = useState(null);  // {stage, progress, status, error?}

  const mintRecoveryToken = async () => {
    setMintingToken(true);
    try {
      // GET (not POST) — production proxies have blocked POST to this
      // exact path in the past. GET matches the working /admin/data/export
      // pattern.
      const r = await axios.get(
        `${API}/admin/data/recovery-token`,
        { headers: getAuthHeader() },
      );
      // Build the recovery URL client-side so it always uses the exact
      // domain the admin is currently on (nsecltd.co.uk in prod, preview
      // URL in preview). The backend's own attempt at this can be wrong
      // because the K8s ingress rewrites the Host header to an internal
      // cluster name.
      const token = r.data?.token;
      if (!token) {
        toast.error('Mint failed — backend returned no token');
        setMintingToken(false);
        return;
      }
      const url = r.data.recovery_url || `${window.location.origin}/api/admin/data/recovery/${token}`;
      setRecoveryUrl(url);
      setRecoveryExpiresAt(r.data.expires_at);
      toast.success(`Recovery URL minted — valid for ${r.data.ttl_minutes} min`);
    } catch (e) {
      // Surface the real reason rather than a generic "Failed" toast so
      // any future regression is debuggable from the UI alone.
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const reason = detail
        ? `(${status}) ${detail}`
        : status
          ? `HTTP ${status} ${e.response?.statusText || ''}`.trim()
          : `network error: ${e.message || 'unknown'}`;
      toast.error(`Mint failed — ${reason}`);
    }
    setMintingToken(false);
  };

  const copyRecoveryUrl = async () => {
    try {
      await navigator.clipboard.writeText(recoveryUrl);
      toast.success('Recovery URL copied to clipboard');
    } catch {
      toast.error('Copy failed — select & copy manually');
    }
  };

  const pullFromUrl = async (mode) => {
    if (!pullUrl.trim()) { toast.error('Paste a recovery URL first'); return; }
    setPulling(true);
    setPullProgress({ status: 'starting', stage: 'starting…', progress: 0 });
    try {
      const start = await axios.post(
        `${API}/admin/data/pull-from-url`,
        { url: pullUrl.trim(), mode },
        { headers: getAuthHeader() },
      );
      const jobId = start.data.job_id;

      // Poll job status every 2s. Each poll is a tiny request so the
      // platform ingress can't timeout this even if the pull takes 5 min.
      let attempts = 0;
      while (attempts < 300) {  // 300 * 2s = 10 min ceiling
        await new Promise(res => setTimeout(res, 2000));
        attempts += 1;
        let j;
        try {
          const jr = await axios.get(`${API}/admin/data/jobs/${jobId}`, { headers: getAuthHeader() });
          j = jr.data;
        } catch (e) {
          // If status polling errors transiently, keep trying.
          continue;
        }
        setPullProgress(j);
        if (j.status === 'done') {
          setLastImport({ mode, counts: j.result?.counts || {} });
          const when = j.result?.exported_at ? new Date(j.result.exported_at).toLocaleString('en-GB') : '';
          toast.success(`Recovery complete${when ? ` — snapshot from ${when}` : ''}`);
          setPullUrl('');
          setConfirmPull(false);
          setPulling(false);
          loadStats();
          return;
        }
        if (j.status === 'error') {
          toast.error(`Recovery failed: ${j.error || 'unknown error'}`);
          setPulling(false);
          return;
        }
      }
      toast.error('Recovery still running after 10 minutes — please refresh and check stats');
    } catch (e) {
      // Surface as much detail as possible: detail field, HTTP status, or message.
      const detail = e.response?.data?.detail
        || (e.response ? `HTTP ${e.response.status} ${e.response.statusText || ''}` : null)
        || e.message
        || 'Pull failed';
      toast.error(`Pull failed: ${detail}`);
    }
    setPulling(false);
  };

  const runEmailDiagnostic = async () => {
    setEmailTesting(true);
    try {
      const r = await axios.get(`${API}/admin/email/diagnostic`, { headers: getAuthHeader() });
      setEmailDiag(r.data);
      const id = r.data?.test_send_result?.id;
      if (id) toast.success(`Test email sent (Resend id ${id.slice(0, 8)}…)`);
      else toast.error('Test send failed — see details below');
    } catch (e) {
      setEmailDiag({ error: e.response?.data?.detail || e.message });
      toast.error('Diagnostic failed');
    }
    setEmailTesting(false);
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/data/stats`, { headers: getAuthHeader() });
      setStats(r.data);
    } catch {
      toast.error('Failed to load data stats');
    }
    try {
      const r = await axios.get(`${API}/admin/data/bundle-info`, { headers: getAuthHeader() });
      setBundleInfo(r.data);
    } catch {
      setBundleInfo({ exists: false });
    }
    setLoading(false);
  };
  useEffect(() => { loadStats(); }, []);

  const handleExport = async (includeBinaries = false) => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/data/export?include_binaries=${includeBinaries ? 'true' : 'false'}`, {
        headers: getAuthHeader(),
        responseType: 'blob',
        timeout: 300000,
      });
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `nsec-snapshot-${includeBinaries ? 'full' : 'content'}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Snapshot downloaded (${includeBinaries ? 'full incl. images & PDFs' : 'content-only'})`);
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.response ? `HTTP ${e.response.status}` : null)
        || e.message
        || 'Export failed';
      toast.error(`Export failed: ${detail}`);
    }
    setDownloading(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error('Please choose a .json snapshot');
      e.target.value = '';
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/admin/data/import?mode=${importMode}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setLastImport(r.data);
      toast.success(`Imported (${importMode}) successfully`);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  const handleBundleSync = async (mode) => {
    setBundleSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/data/sync-from-bundle?mode=${mode}`, null, { headers: getAuthHeader() });
      setLastImport(r.data);
      toast.success(`Bundled snapshot applied (${mode})`);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
    setBundleSyncing(false);
  };

  if (loading) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div></div>;

  return (
    <div data-testid="data-export-import">
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-slate-900 flex items-center gap-2">
          <Database size={26} weight="duotone" className="text-lime-600" />
          Data Export &amp; Import
        </h1>
        <p className="text-slate-500 text-sm">Snapshot all CMS content (text, photos, icons, PDFs) so you can mirror Preview to Production after a deploy.</p>
      </div>

      {/* Email pipeline diagnostic */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-3 rounded-xl bg-amber-100 text-amber-700 shrink-0">
            <Envelope size={24} weight="duotone" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-semibold text-slate-900 mb-1">Email pipeline diagnostic</h3>
            <p className="text-sm text-slate-600 mb-2">Test Resend connectivity in this environment. Reveals the API key prefix the backend is using and sends a real test email to the configured inbox.</p>
          </div>
          <Button
            onClick={runEmailDiagnostic}
            disabled={emailTesting}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="email-diagnostic-btn"
          >
            <Envelope size={15} className="mr-1" />
            {emailTesting ? 'Testing…' : 'Run diagnostic'}
          </Button>
        </div>
        {emailDiag && (
          <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono space-y-1" data-testid="email-diag-result">
            <pre className="whitespace-pre-wrap break-words">{JSON.stringify(emailDiag, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Cross-environment recovery — server-to-server pull */}
      <div className="bg-gradient-to-br from-lime-50 to-white rounded-xl border-2 border-lime-300 p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-3 rounded-xl bg-lime-600 text-white shrink-0">
            <ShareNetwork size={24} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 mb-1">Recover this environment from another (recommended)</h3>
            <p className="text-sm text-slate-700">
              Server-to-server transfer — no file upload, no git, works for any snapshot size. Mint a one-time URL in the source environment, paste it here in the target.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* STEP 1 — Mint URL */}
          <div className="bg-white rounded-xl border border-lime-200 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-lime-700 mb-1">Step 1 · in the source env</p>
            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><ShareNetwork size={18} weight="duotone" className="text-lime-600" /> Mint a recovery URL</h4>
            <p className="text-xs text-slate-600 mb-3">Generates a one-time-use URL exposing this environment&apos;s snapshot. Valid for 60 min.</p>
            <Button
              onClick={mintRecoveryToken}
              disabled={mintingToken}
              className="bg-lime-600 hover:bg-lime-700 w-full"
              data-testid="mint-recovery-url-btn"
            >
              <ShareNetwork size={15} className="mr-1" />
              {mintingToken ? 'Minting…' : (recoveryUrl ? 'Mint a new URL' : 'Mint recovery URL')}
            </Button>
            {recoveryUrl && (
              <div className="mt-3 p-3 rounded-lg bg-lime-50 border border-lime-200 space-y-2" data-testid="recovery-url-result">
                <p className="text-[11px] font-semibold text-lime-700">Copy this URL and paste it into the target environment&apos;s Step 2 box:</p>
                <div className="flex items-stretch gap-1">
                  <input
                    type="text"
                    readOnly
                    value={recoveryUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 text-[11px] font-mono px-2 py-1.5 rounded border border-lime-200 bg-white"
                    data-testid="recovery-url-input"
                  />
                  <Button
                    size="sm"
                    onClick={copyRecoveryUrl}
                    className="bg-lime-600 hover:bg-lime-700 px-2 shrink-0"
                    data-testid="copy-recovery-url-btn"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
                {recoveryExpiresAt && (
                  <p className="text-[10px] text-lime-700">Expires {new Date(recoveryExpiresAt).toLocaleString('en-GB')} · single-use</p>
                )}
              </div>
            )}
          </div>

          {/* STEP 2 — Pull from URL */}
          <div className="bg-white rounded-xl border border-amber-200 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-1">Step 2 · in the target env</p>
            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><ArrowsClockwise size={18} weight="duotone" className="text-amber-600" /> Pull from a recovery URL</h4>
            <p className="text-xs text-slate-600 mb-3">Backend fetches the snapshot directly — your browser never touches the file.</p>
            <input
              type="text"
              value={pullUrl}
              onChange={(e) => setPullUrl(e.target.value)}
              placeholder="Paste recovery URL from source env"
              className="w-full text-xs font-mono px-2 py-1.5 rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-400 mb-2"
              data-testid="pull-url-input"
            />
            {!confirmPull ? (
              <Button
                onClick={() => setConfirmPull(true)}
                disabled={!pullUrl.trim() || pulling}
                className="bg-amber-600 hover:bg-amber-700 w-full"
                data-testid="pull-from-url-reveal-btn"
              >
                <ArrowsClockwise size={15} className="mr-1" /> Pull and apply…
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-[11px] text-amber-800 p-2 rounded bg-amber-50 border border-amber-200">
                  This will <strong>replace all content</strong> in this environment with whatever the URL returns. A safety backup of the current state will be taken first.
                </p>
                <Button
                  onClick={() => pullFromUrl('overwrite')}
                  disabled={pulling}
                  className="bg-red-600 hover:bg-red-700 w-full"
                  data-testid="pull-from-url-overwrite-btn"
                >
                  {pulling ? 'Pulling & applying…' : 'Yes — overwrite this environment'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => pullFromUrl('merge')}
                  disabled={pulling}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 w-full"
                  data-testid="pull-from-url-merge-btn"
                >
                  Merge missing records only
                </Button>
                {pullProgress && pulling && (
                  <div className="mt-2 p-2 rounded bg-slate-50 border border-slate-200 text-xs" data-testid="pull-progress">
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-700">{pullProgress.stage || pullProgress.status || 'working…'}</span>
                      <span className="text-slate-500 font-mono">{pullProgress.progress ?? 0}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-lime-500 transition-all duration-500" style={{ width: `${pullProgress.progress ?? 0}%` }}></div>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmPull(false)}
                  className="text-xs text-slate-500 hover:text-slate-700 underline w-full"
                  data-testid="pull-from-url-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sync from bundled snapshot — recovery tool */}
      <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl border-2 border-amber-300 p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-3 rounded-xl bg-amber-600 text-white shrink-0">
            <Warning size={24} weight="fill" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-semibold text-slate-900">Recovery — sync from bundled snapshot</h3>
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">Use sparingly</span>
            </div>
            <p className="text-sm text-slate-700 mb-3">
              This is the snapshot that shipped with <strong>this deployment build</strong>. It is meant for <strong>one-time disaster recovery</strong> on a freshly deployed empty environment — <strong>not</strong> for keeping production in sync with preview.
            </p>
            {bundleInfo?.exists === false && (
              <p className="text-xs text-red-700 font-medium">No bundled snapshot was found in this deployment.</p>
            )}
            {bundleInfo?.exists && !bundleInfo.error && (
              <div className="text-xs text-slate-600 space-y-1 mb-2">
                <p><span className="font-semibold text-slate-700">Bundled at:</span> {bundleInfo.exported_at ? new Date(bundleInfo.exported_at).toLocaleString('en-GB') : 'unknown'}</p>
                <p><span className="font-semibold text-slate-700">Contains:</span> {bundleInfo.total_records} records ({(bundleInfo.size_bytes / (1024 * 1024)).toFixed(0)} MB)</p>
              </div>
            )}
            <div className="mt-2 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
              <strong>Overwrite will replace</strong> all News, Case Studies, Community, Services, Jobs, Locations, Page Content, Photos, PDFs, HR Documents, Departments and Announcements in this environment with the bundled copy above. Anything edited in production since the snapshot was bundled will be <strong>permanently lost</strong>.
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto">
            {!confirmBundleSync ? (
              <Button
                onClick={() => setConfirmBundleSync(true)}
                disabled={bundleSyncing || bundleInfo?.exists === false}
                className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto"
                data-testid="bundle-sync-reveal-btn"
              >
                I need to recover…
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => handleBundleSync('overwrite')}
                  disabled={bundleSyncing}
                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                  data-testid="bundle-sync-overwrite-btn"
                >
                  {bundleSyncing ? 'Syncing…' : 'Yes — overwrite this environment'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleBundleSync('merge')}
                  disabled={bundleSyncing}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 w-full sm:w-auto"
                  data-testid="bundle-sync-merge-btn"
                >
                  Merge missing records only
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirmBundleSync(false)}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  data-testid="bundle-sync-cancel-btn"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Current data stats */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Current content in this environment</h3>
          <span className="text-xs text-slate-500">{stats?._total || 0} records total</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(COLLECTION_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
              <span className="text-sm text-slate-700 truncate">{label}</span>
              <span className="text-sm font-semibold text-lime-700 ml-2 shrink-0">{stats?.[key] ?? 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Export */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-3 rounded-xl bg-lime-100 text-lime-700 shrink-0">
            <DownloadSimple size={24} weight="duotone" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-semibold text-slate-900 mb-1">Export snapshot</h3>
            <p className="text-sm text-slate-600 mb-2">A JSON file you can re-import on another environment. <strong>Content-only</strong> is recommended for syncing (Preview ↔ Production) — small, fast, and won&apos;t disturb the live site while running.</p>
            <p className="text-xs text-slate-500">Uploaded images and PDFs make a full snapshot 150 MB+ and can briefly slow the public site while exporting. Only use <em>Full</em> if you specifically need to mirror media too.</p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              onClick={() => handleExport(false)}
              disabled={downloading}
              className="bg-lime-600 hover:bg-lime-700"
              data-testid="export-content-btn"
            >
              <DownloadSimple size={15} className="mr-1" />
              {downloading ? 'Preparing…' : 'Content only'}
            </Button>
            <Button
              onClick={() => handleExport(true)}
              disabled={downloading}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="export-full-btn"
            >
              Full (incl. media)
            </Button>
          </div>
        </div>
      </div>

      {/* Import */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-3 rounded-xl bg-amber-100 text-amber-700 shrink-0">
            <UploadSimple size={24} weight="duotone" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-semibold text-slate-900 mb-1">Import snapshot</h3>
            <p className="text-sm text-slate-600 mb-3">Upload a previously exported snapshot to populate this environment.</p>

            <div className="flex flex-wrap gap-2 mb-4">
              <button
                type="button"
                onClick={() => setImportMode('merge')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${importMode === 'merge' ? 'bg-lime-600 text-white border-lime-600' : 'bg-white text-slate-700 border-slate-200 hover:border-lime-400'}`}
                data-testid="import-mode-merge"
              >
                Merge (recommended)
              </button>
              <button
                type="button"
                onClick={() => setImportMode('overwrite')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${importMode === 'overwrite' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-700 border-slate-200 hover:border-red-400'}`}
                data-testid="import-mode-overwrite"
              >
                Overwrite (destructive)
              </button>
            </div>

            {importMode === 'overwrite' && (
              <div className="flex items-start gap-2 px-3 py-2 mb-4 bg-red-50 border border-red-200 rounded-lg">
                <Warning size={16} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">Overwrite will <strong>delete all current content</strong> in the listed collections before loading the snapshot. Use only on an environment you intend to fully replace.</p>
              </div>
            )}
          </div>
          <label className="cursor-pointer shrink-0">
            <Button
              variant="outline"
              size="default"
              asChild
              disabled={importing}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <span><UploadSimple size={15} className="mr-1" />{importing ? 'Importing…' : 'Choose snapshot file'}</span>
            </Button>
            <input
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleImport}
              data-testid="import-snapshot-file"
            />
          </label>
        </div>

        {lastImport && (
          <div className="mt-5 p-4 bg-lime-50 border border-lime-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2 font-medium text-lime-800">
              <CheckCircle size={18} weight="fill" />
              Last import — mode: {lastImport.mode}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-slate-700">
              {Object.entries(lastImport.counts || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between bg-white px-2 py-1 rounded border border-lime-100">
                  <span className="truncate mr-2">{COLLECTION_LABELS[k] || k}</span>
                  <span className="font-mono text-lime-700 shrink-0">
                    {typeof v === 'number' ? v : `+${v.inserted} / ~${v.updated}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 space-y-2">
        <p className="font-semibold text-slate-800">Recommended workflow (post-recovery)</p>
        <p>Production is the source of truth for <strong>content</strong> (news, case studies, community articles, jobs, locations, photos, text). Preview is the source of truth for <strong>code &amp; features</strong>.</p>
        <p>Future redeploys will <strong>not</strong> touch production content — the auto-seed is permanently disabled. If you ever want to pull production content back into preview for testing, click <em>Download snapshot</em> in production, then upload it here in preview.</p>
        <p>Take a fresh backup in Admin → <strong>Backups</strong> before any large content edits.</p>
      </div>
    </div>
  );
}
